import { Component, OnInit } from '@angular/core';
import { ReportService, Coleccion, ColeccionResponse, Producto, ProductoDisponible } from './../../services/report.service';

declare let swal: any;

@Component({
  selector: 'app-coleccion',
  templateUrl: './coleccion.component.html',
  styleUrls: ['./coleccion.component.css']
})
export class ColeccionComponent implements OnInit {

  // Propiedades para la tabla
  colecciones: ColeccionResponse[] = [];
  coleccionesFiltradas: ColeccionResponse[] = [];
  loading = false;
  error = '';

  // Propiedades para filtros de búsqueda
  fechaInicio = '';
  fechaFin = '';
  busqueda = '';

  // Propiedades para el modal de creación/edición
  showModal = false;
  isEditMode = false;
  selectedColeccionId: number | null = null;

  // Propiedades del formulario
  nuevaColeccion: Coleccion = {
    name: '',
    description: '',
    productos: []
  };

  // Propiedades para búsqueda de productos
  productosDisponibles: ProductoDisponible[] = [];
  busquedaProducto = '';
  showProductSearch = false;
  loadingProducts = false;

  // Propiedades para paginación
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;

  constructor(private reportService: ReportService) { }

  ngOnInit() {
    this.cargarColecciones();
  }

  /**
   * Carga todas las colecciones desde el API
   */
  cargarColecciones() {
    this.loading = true;
    this.error = '';

    this.reportService.listarColeccionesEan().subscribe(
      (response) => {
        console.log('Response from API:', response);

        let colecciones: ColeccionResponse[];

        if (Array.isArray(response)) {
          colecciones = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          colecciones = response.data;
        } else if (response && response.colecciones && Array.isArray(response.colecciones)) {
          colecciones = response.colecciones;
        } else if (response && response.results && Array.isArray(response.results)) {
          colecciones = response.results;
        } else if (response && response.filtros && Array.isArray(response.filtros)) {
          colecciones = response.filtros.map((filtro: any) => ({
            id: filtro.id,
            name: filtro.name,
            description: filtro.description,
            productos: [],
            total_codes: filtro.total_codes || 0,
            created_at: filtro.created_at,
            updated_at: filtro.updated_at || filtro.created_at
          }));
        } else {
          console.warn('Unexpected API response format:', response);
          colecciones = [];
        }

        this.colecciones = colecciones;
        this.coleccionesFiltradas = [...this.colecciones];
        this.totalItems = this.colecciones.length;
        this.loading = false;
      },
      (error) => {
        console.error('Error al cargar colecciones:', error);
        this.error = 'Error al cargar las colecciones';
        this.colecciones = [];
        this.coleccionesFiltradas = [];
        this.totalItems = 0;
        this.loading = false;
      }
    );
  }

  /**
   * Aplica filtros de búsqueda
   */
  aplicarFiltros() {
    let resultado = [...this.colecciones];

    if (this.fechaInicio) {
      resultado = resultado.filter(col =>
        new Date(col.created_at) >= new Date(this.fechaInicio)
      );
    }

    if (this.fechaFin) {
      resultado = resultado.filter(col =>
        new Date(col.created_at) <= new Date(this.fechaFin)
      );
    }

    if (this.busqueda.trim()) {
      const termino = this.busqueda.toLowerCase().trim();
      resultado = resultado.filter(col =>
        col.name.toLowerCase().includes(termino) ||
        col.description.toLowerCase().includes(termino)
      );
    }

    this.coleccionesFiltradas = resultado;
    this.totalItems = resultado.length;
    this.currentPage = 1;
  }

  /**
   * Limpia todos los filtros
   */
  limpiarFiltros() {
    this.fechaInicio = '';
    this.fechaFin = '';
    this.busqueda = '';
    this.coleccionesFiltradas = [...this.colecciones];
    this.totalItems = this.colecciones.length;
    this.currentPage = 1;
  }

  /**
   * Abre el modal para crear nueva colección
   */
  nuevaColeccionModal() {
    this.isEditMode = false;
    this.selectedColeccionId = null;
    this.nuevaColeccion = {
      name: '',
      description: '',
      productos: []
    };
    this.showModal = true;
  }

  /**
   * Abre el modal para editar colección existente
   * Carga los datos completos de la colección incluyendo productos
   */
  editarColeccion(coleccion: ColeccionResponse) {
    this.loading = true;

    // Obtener el detalle completo de la colección para cargar los productos
    this.reportService.obtenerColeccionEan(coleccion.id).subscribe(
      (response: ColeccionResponse) => {
        this.isEditMode = true;
        this.selectedColeccionId = coleccion.id;
        this.nuevaColeccion = {
          name: response.name,
          description: response.description,
          productos: [...response.productos]
        };
        this.showModal = true;
        this.loading = false;
      },
      (error) => {
        console.error('Error al cargar detalle de colección:', error);
        this.loading = false;
        this.mostrarError('No se pudo cargar el detalle de la colección');
      }
    );
  }

  /**
   * Cierra el modal
   */
  cerrarModal() {
    this.showModal = false;
    this.showProductSearch = false;
    this.busquedaProducto = '';
    this.productosDisponibles = [];
  }

  /**
   * Guarda la colección (crear o actualizar)
   */
  guardarColeccion() {
    if (!this.validarFormulario()) {
      return;
    }

    this.loading = true;

    if (this.isEditMode && this.selectedColeccionId) {
      this.reportService.actualizarColeccionEan(
        this.selectedColeccionId,
        this.nuevaColeccion.productos
      ).subscribe(
        (response) => {
          this.loading = false;
          this.cerrarModal();
          this.cargarColecciones();
          this.mostrarExito('Colección actualizada exitosamente');
        },
        (error) => {
          console.error('Error al actualizar colección:', error);
          this.loading = false;
          this.mostrarError('Error al actualizar la colección');
        }
      );
    } else {
      this.reportService.crearColeccionEan(this.nuevaColeccion).subscribe(
        (response) => {
          this.loading = false;
          this.cerrarModal();
          this.cargarColecciones();
          this.mostrarExito('Colección creada exitosamente');
        },
        (error) => {
          console.error('Error al crear colección:', error);
          this.loading = false;
          this.mostrarError('Error al crear la colección');
        }
      );
    }
  }

  /**
   * Valida el formulario antes de guardar
   */
  validarFormulario(): boolean {
    if (!this.nuevaColeccion.name.trim()) {
      this.mostrarError('El nombre de la colección es obligatorio');
      return false;
    }
    if (!this.nuevaColeccion.description.trim()) {
      this.mostrarError('La descripción es obligatoria');
      return false;
    }
    if (this.nuevaColeccion.productos.length === 0) {
      this.mostrarError('Debe agregar al menos un producto');
      return false;
    }
    return true;
  }

  /**
   * Busca productos disponibles
   */
  buscarProductos() {
    if (!this.busquedaProducto.trim()) {
      this.productosDisponibles = [];
      return;
    }

    this.loadingProducts = true;
    this.reportService.buscarProductosEan(this.busquedaProducto).subscribe({
      next: (response: any) => {
        console.log('Products response:', response);
        const productos = response.response.data || [];
        this.productosDisponibles = productos;
        this.loadingProducts = false;
      },
      error: (error) => {
        console.error('Error al buscar productos:', error);
        this.loadingProducts = false;
        this.productosDisponibles = [];
      }
    });
  }

  /**
   * Agrega un producto a la colección
   */
  agregarProducto(producto: ProductoDisponible) {
    const existe = this.nuevaColeccion.productos.find(p => p.ean_code === producto.ean_code);
    if (existe) {
      this.mostrarError('Este producto ya está en la colección');
      return;
    }

    this.nuevaColeccion.productos.push({
      ean_code: producto.ean_code,
      product_name: producto.product_name
    });

    this.productosDisponibles = [];
    this.busquedaProducto = '';
  }

  /**
   * Elimina un producto de la colección
   */
  eliminarProducto(index: number) {
    this.nuevaColeccion.productos.splice(index, 1);
  }

  /**
   * Elimina una colección completa
   */
  eliminarColeccion(coleccion: ColeccionResponse) {
    this.mostrarConfirmacion(
      `¿Estás seguro de eliminar la colección "${coleccion.name}"? Esta acción no se puede deshacer.`,
      () => {
        this.loading = true;
        this.reportService.eliminarColeccionEan(coleccion.id).subscribe(
          (response) => {
            this.loading = false;
            this.cargarColecciones();
            this.mostrarExito('La colección ha sido eliminada exitosamente');
          },
          (error) => {
            console.error('Error al eliminar colección:', error);
            this.loading = false;
            this.mostrarError('Error al eliminar la colección');
          }
        );
      }
    );
  }

  /**
   * Obtiene los elementos de la página actual para mostrar
   */
  get elementosPaginaActual(): ColeccionResponse[] {
    const inicio = (this.currentPage - 1) * this.itemsPerPage;
    const fin = inicio + this.itemsPerPage;
    return this.coleccionesFiltradas.slice(inicio, fin);
  }

  /**
   * Calcula el número total de páginas
   */
  get totalPaginas(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  /**
   * Cambia a una página específica
   */
  cambiarPagina(pagina: number) {
    if (pagina >= 1 && pagina <= this.totalPaginas) {
      this.currentPage = pagina;
    }
  }

  /**
   * Obtiene array de números de páginas para mostrar
   */
  get paginasVisibles(): number[] {
    const paginas = [];
    const total = this.totalPaginas;
    const actual = this.currentPage;

    let inicio = Math.max(1, actual - 3);
    let fin = Math.min(total, actual + 3);

    if (fin - inicio < 6) {
      if (inicio === 1) {
        fin = Math.min(total, inicio + 6);
      } else {
        inicio = Math.max(1, fin - 6);
      }
    }

    for (let i = inicio; i <= fin; i++) {
      paginas.push(i);
    }

    return paginas;
  }

  /**
   * Formatea fecha para mostrar
   */
  formatearFecha(fecha: string): string {
    return new Date(fecha).toLocaleDateString('es-CO');
  }

  /**
   * Métodos auxiliares para manejar notificaciones
   */
  private mostrarError(mensaje: string): void {
    if (typeof swal !== 'undefined') {
      swal({
        title: 'Error',
        text: mensaje,
        icon: 'error',
        button: 'Aceptar'
      });
    } else {
      alert('Error: ' + mensaje);
    }
  }

  private mostrarExito(mensaje: string): void {
    if (typeof swal !== 'undefined') {
      swal({
        title: 'Éxito',
        text: mensaje,
        icon: 'success',
        button: 'Aceptar'
      });
    } else {
      alert('Éxito: ' + mensaje);
    }
  }

  private mostrarConfirmacion(mensaje: string, callback: () => void): void {
    if (typeof swal !== 'undefined') {
      swal({
        title: 'Confirmar',
        text: mensaje,
        icon: 'warning',
        buttons: ['Cancelar', 'Eliminar'],
        dangerMode: true,
      }).then((willDelete) => {
        if (willDelete) {
          callback();
        }
      });
    } else {
      if (confirm(mensaje)) {
        callback();
      }
    }
  }
}
